import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { useI18n } from "../i18n/I18nProvider";
import MagneticButton from "../components/MagneticButton";
import SEO from "../components/SEO";
import { getSiteOrigin } from "../siteOrigin";

export default function PricingPage() {
  const { t, lang } = useI18n();
  const SITE = getSiteOrigin();
  const PRICES = { Essential: 1800, Essentiel: 1800, Signature: 4500, Cinema: 9000, Cinéma: 9000 };
  const schema = [{
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: lang === "fr" ? "Forfaits de production Phaneroo Prod" : "Phaneroo Prod production packages",
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    provider: { "@id": `${SITE}/#studio` },
    itemListElement: t.pricing.tiers.map((tier) => ({
      "@type": "Offer",
      name: tier.name,
      description: tier.desc,
      priceCurrency: "CAD",
      price: PRICES[tier.name] || undefined,
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "CAD",
        price: PRICES[tier.name] || undefined,
        valueAddedTaxIncluded: false,
      },
      availability: "https://schema.org/InStock",
      url: `${SITE}/pricing`,
    })),
  }, {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: lang === "fr" ? "Tarifs" : "Pricing", item: `${SITE}/pricing` },
    ],
  }];
  return (
    <main className="pt-40 lg:pt-48 pb-32 bg-[#050505] min-h-screen" data-testid="page-pricing">
      <SEO
        title={lang === "fr"
          ? "Tarifs Production Vidéo & Photo Moncton NB — Forfaits CAD"
          : "Video & Photo Production Pricing Moncton NB — CAD Packages"}
        description={lang === "fr"
          ? "Tarifs de production vidéo et photo à Moncton, NB. Forfait Essentiel à partir de 1 800 $ CAD, Signature à partir de 4 500 $ CAD, Cinéma sur mesure. Cession des droits complète incluse. HST 15 % en sus au N.-B."
          : "Video and photo production pricing in Moncton, NB. Essential package from CAD $1,800, Signature from CAD $4,500, Cinema custom. Full commercial rights included. 15% HST extra in NB."}
        path="/pricing"
        lang={lang}
        schema={schema}
      />
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
            <span className="block w-8 h-px bg-rouge" /><span>{t.pricing.eyebrow}</span>
          </div>
          <h1 className="mt-6 font-serif-editorial text-5xl sm:text-6xl lg:text-7xl text-ink-100 tracking-tighter leading-[1.02] max-w-4xl">
            {t.pricing.title}
          </h1>
          <p className="mt-6 max-w-2xl text-ink-300 text-base lg:text-lg">{t.pricing.sub}</p>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 border-t border-hair">
          {t.pricing.tiers.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.08} className={`relative ${tier.featured ? "tracing-border" : "bg-surface"} ${i !== 0 ? "md:border-l md:border-hair" : ""}`}>
              <div className={`p-8 lg:p-10 h-full flex flex-col ${tier.featured ? "lg:-translate-y-4" : ""}`} data-testid={`pricing-tier-${i}`}>
                <div className="flex items-center justify-between">
                  <h2 className="font-serif-editorial text-3xl text-ink-100">{tier.name}</h2>
                  {tier.featured && <span className="text-[10px] tracking-[0.24em] uppercase text-rouge">★ Populaire</span>}
                </div>
                <div className="mt-6 font-serif-editorial text-4xl lg:text-5xl text-ink-100 leading-none">{tier.price}</div>
                <p className="mt-4 text-ink-300 text-sm leading-relaxed min-h-[64px]">{tier.desc}</p>
                <ul className="mt-8 space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-ink-100 text-[14px] leading-relaxed">
                      <Check size={14} className="text-rouge mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <MagneticButton
                  as={Link}
                  to="/contact"
                  data-testid={`pricing-cta-${i}`}
                  data-cursor={t.pricing.cta}
                  className={`group mt-10 inline-flex items-center justify-center gap-3 px-6 py-4 text-[11px] tracking-[0.24em] uppercase transition-colors ${
                    tier.featured ? "bg-rouge hover:bg-rouge-deep text-ink-100" : "border border-ink-500 hover:border-ink-100 text-ink-100"
                  }`}
                >
                  {t.pricing.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </MagneticButton>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
